import React, { useCallback, useEffect, useState } from 'react';
import api from './api';
import { formatDate, taskCode, STATUS_LABELS, useFermerAvecEchap } from './helpers';

/**
 * Regroupement des tâches jumelles.
 *
 * Un projet importé depuis une liste de besoins finit souvent avec dix lignes
 * « Approvisionnement … » aux mêmes dates et au même responsable. Ce ne sont
 * pas dix tâches : c'est une tâche et dix points de checklist. L'écran propose
 * les regroupements évidents, laisse décocher ce qui n'a rien à y faire,
 * renommer la tâche mère, et — surtout — défaire.
 *
 * Rien n'est supprimé : les tâches d'origine sont masquées, pas effacées. On
 * peut donc revenir en arrière sans rien perdre.
 */
function MergeTasksModal({ project, onClose, onDone }) {
  useFermerAvecEchap(onClose);
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [envoi, setEnvoi] = useState(false);

  // Par proposition : les tâches cochées et l'intitulé retenu.
  const [choix, setChoix] = useState({});

  const charger = useCallback(() => {
    setChargement(true);
    api
      .get(`/projects/${project.id}/regroupements`)
      .then((res) => {
        setDonnees(res.data);
        const initial = {};
        for (const p of res.data.propositions) {
          initial[p.cle] = {
            nom: p.nom_propose,
            ids: p.taches.map((t) => t.id),
          };
        }
        setChoix(initial);
      })
      .catch(() => setErreur('Impossible d’analyser les tâches du projet.'))
      .finally(() => setChargement(false));
  }, [project.id]);

  useEffect(() => { charger(); }, [charger]);

  const basculer = (cle, id) =>
    setChoix((c) => {
      const actuel = c[cle];
      const ids = actuel.ids.includes(id)
        ? actuel.ids.filter((x) => x !== id)
        : [...actuel.ids, id];
      return { ...c, [cle]: { ...actuel, ids } };
    });

  const regrouper = (proposition) => {
    const c = choix[proposition.cle];
    if (!c || c.ids.length < 2) {
      return setErreur('Il faut au moins deux tâches cochées pour former un regroupement.');
    }
    if (!c.nom.trim()) return setErreur('Donnez un intitulé à la tâche regroupée.');

    setErreur(''); setMessage(''); setEnvoi(true);
    api
      .post(`/projects/${project.id}/regrouper`, {
        task_ids: c.ids,
        nom: c.nom.trim(),
        assigned_to: proposition.assigned_to || undefined,
      })
      .then((res) => {
        setMessage(
          `${res.data.regroupees} tâches regroupées sous « ${c.nom.trim()} ». ` +
            'Chacune est devenue un point de la checklist.'
        );
        charger();
        if (onDone) onDone();
      })
      .catch((err) => setErreur(err.response?.data?.message || 'Le regroupement a échoué.'))
      .finally(() => setEnvoi(false));
  };

  const degrouper = (existant) => {
    if (!window.confirm(
      `Défaire « ${existant.name} » ? Les ${existant.total} tâches d’origine réapparaîtront ` +
      'telles qu’elles étaient, avec leurs commentaires et leurs pièces jointes.'
    )) return;

    setErreur(''); setMessage(''); setEnvoi(true);
    api
      .post(`/projects/${project.id}/degrouper`, { task_id: existant.task_id })
      .then((res) => {
        setMessage(`${res.data.rendues} tâches rendues à leur état d’origine.`);
        charger();
        if (onDone) onDone();
      })
      .catch((err) => setErreur(err.response?.data?.message || 'L’opération a échoué.'))
      .finally(() => setEnvoi(false));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Regrouper les tâches jumelles</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {erreur && <div className="error-message" style={{ marginBottom: 12 }}>{erreur}</div>}
        {message && <div className="success-message" style={{ marginBottom: 12 }}>{message}</div>}

        {chargement && <p>Analyse des tâches...</p>}

        {!chargement && donnees && (
          <>
            <div className="fusion-intro">
              Dix lignes « Approvisionnement … » aux mêmes dates et au même
              responsable ne sont pas dix tâches : c’est <strong>une</strong> tâche
              et dix points de checklist. Le regroupement raccourcit le Gantt, et
              rend l’avancement honnête — une case cochée vaut un poste livré.
              <br />
              <strong>Rien n’est supprimé.</strong> Les tâches d’origine sont
              masquées, avec leurs commentaires et leurs pièces jointes ; défaire
              le regroupement les rend telles qu’elles étaient.
            </div>

            {donnees.existants.length > 0 && (
              <section className="fusion-section">
                <h3 className="rapport-section-titre">
                  Regroupements en place ({donnees.existants.length})
                </h3>
                <table className="rapport-tableau">
                  <thead>
                    <tr>
                      <th className="rapport-col-ref">Réf.</th>
                      <th>Tâche</th>
                      <th className="rapport-col-droite">Postes</th>
                      <th className="rapport-col-droite">Avancement</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {donnees.existants.map((e) => (
                      <tr key={e.task_id}>
                        <td className="rapport-ref">{taskCode(e.task_id)}</td>
                        <td>{e.name}</td>
                        <td className="rapport-col-droite">{e.total}</td>
                        <td className="rapport-col-droite">{e.progress_percent}%</td>
                        <td style={{ textAlign: 'right' }}>
                          <button className="btn btn-sm" onClick={() => degrouper(e)} disabled={envoi}>
                            Défaire
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}

            <section className="fusion-section">
              <h3 className="rapport-section-titre">
                Regroupements possibles ({donnees.propositions.length})
              </h3>

              {donnees.propositions.length === 0 ? (
                <div className="empty-state" style={{ textAlign: 'left' }}>
                  Aucune série de tâches jumelles détectée. VIGIE ne propose un
                  regroupement qu’à partir de {donnees.minimum} tâches qui partagent
                  le même premier mot, le même responsable et les mêmes dates.
                </div>
              ) : (
                donnees.propositions.map((p) => {
                  const c = choix[p.cle] || { nom: p.nom_propose, ids: [] };
                  return (
                    <div key={p.cle} className="fusion-carte">
                      <div className="fusion-carte-tete">
                        <div style={{ flex: 1, minWidth: 260 }}>
                          <label>Intitulé de la tâche regroupée</label>
                          <input
                            value={c.nom}
                            onChange={(e) =>
                              setChoix((x) => ({ ...x, [p.cle]: { ...c, nom: e.target.value } }))
                            }
                          />
                        </div>
                        <button
                          className="btn btn-primary"
                          onClick={() => regrouper(p)}
                          disabled={envoi || c.ids.length < 2}
                        >
                          Regrouper {c.ids.length} tâches
                        </button>
                      </div>

                      <div className="fusion-carte-meta">
                        {p.assigned_name || 'sans responsable'} ·{' '}
                        {p.start_date ? `du ${formatDate(p.start_date)} ` : ''}
                        au {formatDate(p.due_date)}
                      </div>

                      <div className="fusion-liste">
                        {p.taches.map((t) => (
                          <label key={t.id} className="jalon-case">
                            <input
                              type="checkbox"
                              checked={c.ids.includes(t.id)}
                              onChange={() => basculer(p.cle, t.id)}
                            />
                            <span className="jalon-case-ref">{taskCode(t.id)}</span>
                            <span className="jalon-case-nom">{t.name}</span>
                            <span className="jalon-case-date">
                              {STATUS_LABELS[t.status] || t.status} · {t.progress_percent}%
                            </span>
                          </label>
                        ))}
                      </div>

                      <p className="fusion-note">
                        Décochez ce qui doit rester une tâche à part entière — une
                        ligne qui a son propre responsable, sa propre échéance, ou
                        qu’on veut pouvoir bloquer indépendamment.
                      </p>
                    </div>
                  );
                })
              )}
            </section>
          </>
        )}

        <div className="modal-footer">
          <button className="btn" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

export default MergeTasksModal;
